import { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Formik, FormikProps, Form } from 'formik';
import { FaUser } from 'react-icons/fa';
import { IoMdBriefcase } from 'react-icons/io';
import { VscHome } from 'react-icons/vsc';
import { Divider } from '@mui/material';
import { toast } from 'react-toastify';

import * as CXT from 'context/globalContext';
import Button from 'components/Button';
import MaskedInput from 'components/MaskedInput';
import { cpfMask, unMask } from 'utils/mask';
import { findData } from 'services/findCEP';
import { Person } from 'services/client';
import { Register } from 'services/register';
import { MaritalStatus } from 'services/maritalStatus';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import convertDataToSubmit from '../../Register/utils/convertDataToSubmit';
import { ContainerWithInformation } from '../../Register/components/ComponentWithInformation';
import { InputsAndSelectsOfBasicInformation } from '../../Register/components/InputsAndSelectsOfBasicInformation';
import { InputsForInformationAddress } from '../../Register/components/InputsForInformationAddress';
import { InputsAndSelectsOfProfissionalInformation } from '../../Register/components/InputsAndSelectsOfProfissionalInformation';
import { ButtonsForm } from '../../Register/components/ButtonsForm';
import { FormPropsType } from '../../Register/utils/types/FormPropType/FormPropType';
import {
  returnsWithCapitalLettersAndWithoutAccent,
  removeWhiteSpace,
} from '../../Register/utils/formFunctions/returnsWithCapitalLettersAndWithoutAccent';
import { initialData } from '../../Register/utils/initialData/intialValuesForForm';
import { findEnderecoIndex } from '../../Register/utils/convertDataToSubmit';

import * as S from './styles';
import {
  EstadoCivilProps,
  ProfissaoProps,
  TipoOcupacaoProps,
} from 'pages/Register/utils/personProps';
import { cloneDeep, each, find, size, some } from 'lodash';
import { validation } from 'pages/Register/utils/validation/validation';
import { FormPropsPJType } from 'pages/Register/utils/types/FormPropType/FormPropPJType';

Bugsnag.start({
  apiKey: '00621c4c68a1b2bd34e7458c0f8d29bc',
  plugins: [new BugsnagPluginReact()],
});

type PessoaFisicaFormProps = {
  customerCpf: string;
  role: string;
  municipios: any[];
  submitButtonLabel?: string;
  stopLoadingOnFinish?: boolean;
  finishCallback: (registerResponse: any) => void;
  missingCustomerDataCallback?: () => void;
  onRequestCredito?: () => void;
  extraActions?: React.ReactNode;
};

const PessoaFisicaForm = forwardRef(function PessoaFisicaForm(
  {
    customerCpf: customerCpf,
    role: role,
    municipios: municipios,
    stopLoadingOnFinish = true,
    submitButtonLabel: submitButtonLabel,
    finishCallback: finishCallback,
    missingCustomerDataCallback: missingCustomerDataCallback,
    onRequestCredito: onRequestCredito,
    extraActions: extraActions,
  }: PessoaFisicaFormProps,
  ref
) {
  const globalContext = useContext(CXT.Context);
  const [customer, setCustomer] = useState<any>({});

  const formikRef = useRef<FormikProps<any>>(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      formikRef.current?.submitForm();
    },
  }));

  const [comprovantes, setComprovantes] = useState<ProfissaoProps[]>([]);
  const [profissoes, setProfissoes] = useState<ProfissaoProps[]>([]);
  const [estadosCivis, setEstadosCivis] = useState<EstadoCivilProps[]>([]);
  const [ocupacoes, setOcupationList] = useState<TipoOcupacaoProps[]>([]);
  const [tipoOcupacao, setTipoOcupacao] = useState(0);
  const [ocupacoesTags, setOcupacoesTags] = useState('');
  const [dataAdmissaoInvalidaMsg, setDataAdmissaoInvalidaMsg] = useState('');

  const [successForm, setSuccessForm] = useState({} as FormPropsType);
  const [errorsForm, setErrorsForm] = useState({} as FormPropsType);
  const [formSubmited, setFormSubmited] = useState(false);

  const [initialValues, setInitialValues] = useState<FormPropsType>(
    initialData(undefined, -1, undefined, () => '', [])
  );

  const [nacionalidades, setNacionalidades] = useState<
    { id: number; descricao: string }[]
  >([]);

  useEffect(() => {
    getOptions();
  }, []);

  useEffect(() => {
    loadPersonData();
  }, [customerCpf]);

  useEffect(() => {
    if (tipoOcupacao && ocupacoes) {
      const found = find(
        ocupacoes,
        (it) => Number(it.codigo) === Number(tipoOcupacao)
      );
      if (found) {
        setOcupacoesTags(found.tagValidacao);
      }
    }
  }, [tipoOcupacao, ocupacoes]);

  async function getOptions() {
    MaritalStatus.Get()
      .then((response) => {
        setEstadosCivis(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar os estados civis');
      });

    findData
      .GetTipoOcupação()
      .then((response) => {
        setOcupationList(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        setOcupationList([
          {
            codigo: '1',
            descricao: 'Funcionário de Iniciativa Privada',
            abreviacao: '1',
            tagValidacao:
              '#renda##rp_nome_empresa##rp_municipio##rp_telefone##rp_admissao##profissao#',
          },
          {
            codigo: '10',
            descricao: 'Estudante',
            abreviacao: '10',
            tagValidacao: '#renda#',
          },
        ]);
      });

    Person.JobsRequest()
      .then((response: any) => {
        setProfissoes(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar as profissões');
      });

    Person.ComprovantRequest()
      .then((response: any) => {
        setComprovantes(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar os tipos de comprovantes');
      });

    findData
      .GetNacionalidades()
      .then((response) => {
        setNacionalidades(response.data.data);
      })
      .catch((err) => {
        Bugsnag.notify(err);
        toast.error('Erro ao carregar as nacionalidades');
      });
  }

  const loadPersonData = () => {
    globalContext.setIsLoading(true);
    const unmaskedCpf = unMask(customerCpf, '\\.|-');

    Person.ClientRequest(unmaskedCpf)
      .then(async (response) => {
        const foundPerson = response?.data?.data;

        setCustomer(foundPerson);

        const storageKey = role === 'avalista' ? 'avalista' : 'client';
        localStorage.setItem(storageKey, JSON.stringify(foundPerson));

        let phone: any;

        try {
          if (foundPerson.telefones) {
            phone = foundPerson.telefones.find((item: any) => {
              return item.status === 1 && item.preferencia === true;
            });
          }
        } catch {
          if (missingCustomerDataCallback) {
            missingCustomerDataCallback();
          }
        }

        const getEmail = () => {
          let aux = '';

          foundPerson?.emails?.forEach(
            (value: {
              email: string;
              preferencia: boolean;
              status: number;
            }) => {
              if (value.preferencia === true && value.status === 1) {
                aux = value.email;
              }
            }
          );

          return aux;
        };

        const enderecoIndex = findEnderecoIndex(foundPerson?.enderecos);

        const formData = initialData(
          foundPerson,
          enderecoIndex,
          phone,
          getEmail,
          municipios
        ) as FormPropsType;

        setInitialValues(formData);
        setTipoOcupacao(Number(formData.atividade));
        globalContext.setIsLoading(false);
      })
      .catch((error) => {
        globalContext.setIsLoading(false);
        if (error.response?.status === 404) {
          return toast.error(
            'Cliente não encontrado com esse CPF. Verifique os dados digitados e tente novamente'
          );
        }

        if (error.response?.status === 500) {
          return toast.error(
            'Ops! Tivemos um problema ao prosseguir com a sua solicitação. ' +
              'Tente novamente mais tarde e se o problema persistir contate o suporte!'
          );
        }

        return toast.error(
          'Não foi possível prosseguir com a solicitação. Faça o Login novamente.'
        );
      });
  };

  const normalize = (value: string) => {
    return removeWhiteSpace(returnsWithCapitalLettersAndWithoutAccent(value));
  };

  const findMunicipio = (cidade: string, uf: string) => {
    return find(
      municipios,
      (it) =>
        normalize(it.descricao) === normalize(cidade) &&
        normalize(it.codigoEstado) === normalize(uf)
    );
  };

  const findItem = (
    list: EstadoCivilProps[] | ProfissaoProps[],
    value: string
  ): EstadoCivilProps | ProfissaoProps => {
    const found = find(list, (posicao) => posicao.descricao === value);
    if (!found) throw new Error('Item not found');
    return found;
  };

  const submitPerson = (person: any, formData: FormPropsType) => {
    return new Promise((resolve, error) => {
      const cliente = findMunicipio(formData.cidade, formData.uf);
      const empresa = findMunicipio(formData.cidadeEmpresa, formData.ufEmpresa);
      const municipioEmpresaId = empresa?.id;

      const dataMunicipio = {
        id: cliente.id,
        descricao: returnsWithCapitalLettersAndWithoutAccent(cliente.descricao),
        codigoEstado: normalize(cliente.codigoEstado),
      };

      const estadoCivil = findItem(estadosCivis, formData.estadoCivil);
      const profissao = findItem(profissoes, formData.profissao);
      const comprovacao = findItem(comprovantes, formData.comprovacao);

      const updatedJson = convertDataToSubmit(
        person,
        formData,
        estadoCivil,
        profissao,
        comprovacao,
        dataMunicipio,
        municipioEmpresaId
      );

      const postData = {
        messages: null,
        data: updatedJson,
      };

      Register.Post(postData)
        .then((response) => {
          resolve(response.data.data);
        })
        .catch((err) => {
          Bugsnag.notify(err);
          error();
        });
    });
  };

  const validateCidadeUf = (
    formData: FormPropsType,
    validationTags: string
  ) => {
    const municipioCliente = findMunicipio(formData.cidade, formData.uf);
    if (!municipioCliente) {
      formData.cidade = '';
      formData.uf = '';
      toast.error('Endereço Pessoal: Cidade ou UF inexistente');
    }

    const municipioEmpresa = findMunicipio(
      formData.cidadeEmpresa,
      formData.ufEmpresa
    );
    if (validationTags.match('rp_municipio')) {
      if (!municipioEmpresa) {
        formData.cidadeEmpresa = '';
        formData.ufEmpresa = '';
        toast.error('Referencia Profissional: Cidade ou UF inexistente');
      }
    }
  };

  const isFormValid = (formData: FormPropsType) => {
    validateCidadeUf(formData, ocupacoesTags);
    const resultado = validation(
      formData,
      estadosCivis,
      ocupacoes.map((posicao) => Number(posicao.codigo)),
      comprovantes,
      ocupacoesTags,
      profissoes
    );

    setSuccessForm(resultado[0]);
    setErrorsForm(resultado[1]);

    // index 0 - Success fields
    // index 1 - Error fields
    // index 2 - status

    if (resultado[2]) {
      globalContext.setIsLoading(true);
      return true;
    }
    return false;
  };

  const handleSubmit = (formData: FormPropsType) => {
    if (!isFormValid(formData)) {
      return;
    }

    setFormSubmited(true);

    submitPerson(customer, formData)
      .then((response) => {
        if (stopLoadingOnFinish) {
          toast.success('Salvo com sucesso!');
        }
        finishCallback(response);
      })
      .catch(() => {
        toast.error('Erro ao salvar o cliente');
      })
      .finally(() => {
        if (stopLoadingOnFinish) {
          globalContext.setIsLoading(false);
        }
      });
  };

  const validOnChangeAfterError =
    (
      updateSuccessForm: (data: any) => void,
      updateErrorsForm: (data: any) => void,
      ocupacaoValidationTags: string
    ) =>
    (
      campSelect: string,
      eventTargetValue: string,
      values: FormPropsType | FormPropsPJType
    ) => {
      console.log('validOnChangeAfterError');
      if (
        formSubmited &&
        eventTargetValue !== undefined &&
        ocupacaoValidationTags
      ) {
        console.log('validOnChangeAfterError IN');
        const array = Object.entries(values);
        const arrayWithIndexOneOfArray = array.map((posicao) => posicao[0]);

        if (campSelect === 'mesAnoAdmissao') {
          const newFormatForCampSelect = eventTargetValue.replace('/', '');
          eventTargetValue = newFormatForCampSelect;
        }

        const indexForChange = arrayWithIndexOneOfArray.indexOf(campSelect);
        array[indexForChange][1] = eventTargetValue;

        const changeValueInArray = Object.fromEntries(array);
        const newObjectWithValues = changeValueInArray as FormPropsType;

        values = newObjectWithValues;

        const valid = validation(
          values,
          estadosCivis,
          ocupacoes.map((posicao) => Number(posicao.codigo)),
          comprovantes,
          ocupacaoValidationTags,
          profissoes
        );

        updateSuccessForm(valid[0]);

        updateErrorsForm(valid[1]);
      }
    };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={(values: FormPropsType) => handleSubmit(values)}
      enableReinitialize={true}
    >
      {({ touched, values, setFieldValue }: FormikProps<FormPropsType>) => (
        <Form>
          <S.Content>
            <S.Left>
              <S.BasicInfo>
                <ContainerWithInformation
                  Icon={FaUser}
                  text={'Informações Básicas do Cliente'}
                  identifier={values.cpf}
                  identifierType="cpf"
                />
                <InputsAndSelectsOfBasicInformation
                  isModifying
                  values={values}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  maritalStatusList={estadosCivis}
                  nacionalidades={nacionalidades}
                />
              </S.BasicInfo>
              <S.AddressContact>
                <ContainerWithInformation
                  Icon={VscHome}
                  text={'Endereço e contato'}
                />

                <InputsForInformationAddress
                  values={values}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  validOnChangeAfterError={validOnChangeAfterError(
                    setSuccessForm,
                    setErrorsForm,
                    ocupacoesTags
                  )}
                  formSubmited={formSubmited}
                />
              </S.AddressContact>
            </S.Left>
            <S.Right>
              <S.ProfessionalInfo>
                <ContainerWithInformation
                  Icon={IoMdBriefcase}
                  text={'Informações Profissionais'}
                />
                <InputsAndSelectsOfProfissionalInformation
                  values={values}
                  maritalStatusList={estadosCivis}
                  errorsForm={errorsForm}
                  successForm={successForm}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  validOnChangeAfterError={validOnChangeAfterError(
                    setSuccessForm,
                    setErrorsForm,
                    ocupacoesTags
                  )}
                  listaProfissoes={profissoes}
                  formSubmited={formSubmited}
                  ocupationCurrentTagValidation={ocupacoesTags}
                  ocupationList={ocupacoes}
                  setErrorsForm={setErrorsForm}
                  listaComprovantes={comprovantes}
                  messageErrorValidationDateAdmission={dataAdmissaoInvalidaMsg}
                  setFormSubmited={setFormSubmited}
                  setTipoOcupacao={setTipoOcupacao}
                />
              </S.ProfessionalInfo>
            </S.Right>
          </S.Content>
          <S.HContainer>
            {globalContext.isLoading ? (
              ''
            ) : (
              <S.ButtonsRow>
                <ButtonsForm
                  isModifying
                  submitButtonLabel={submitButtonLabel}
                />

                {extraActions}
              </S.ButtonsRow>
            )}
          </S.HContainer>
        </Form>
      )}
    </Formik>
  );
});

export default PessoaFisicaForm;
